import React, {useState, useEffect} from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
  Chip,
  Divider,
} from "@mui/material";
import {Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon} from "@mui/icons-material";
import {useTranslation} from "react-i18next";
import {
  getHarvestRecords,
  createHarvestRecord,
  updateHarvestRecord,
  getCurrentCycle,
  HarvestRecord,
  CreateHarvestRecordDto,
  UpdateHarvestRecordDto,
  updateCycle,
} from "../../api/pond";
import {useForm} from "../../utils/useForm";
import {FORM_VALIDATION_CONFIGS} from "../../utils/formValidation";
import FormField from "../../components/common/FormField";

interface HarvestTabProps {
  pondId: string;
  cycleId?: string;
  isCycleActive?: boolean;
}

const HarvestTab: React.FC<HarvestTabProps> = ({pondId, cycleId, isCycleActive = true}) => {
  const {t} = useTranslation();
  const [harvestRecords, setHarvestRecords] = useState<HarvestRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<HarvestRecord | null>(null);
  const [currentCycle, setCurrentCycle] = useState<any>(null);

  const form = useForm({
    initialValues: {
      harvestDate: "",
      count: 0,
      totalWeight: 0,
      harvestType: "partial" as "partial" | "final",
      notes: "",
    },
    validationConfig: FORM_VALIDATION_CONFIGS.HARVEST_RECORD,
    onSubmit: async (values) => {
      try {
        if (editingRecord) {
          await updateHarvestRecord(editingRecord.id, values as UpdateHarvestRecordDto);
        } else {
          await createHarvestRecord(pondId, values as CreateHarvestRecordDto);
        }
        fetchHarvestRecords();
        handleCloseModal();
      } catch (err: any) {
        console.error("Error saving harvest record:", err);
        setError(err.response?.data?.message || "Failed to save harvest record");
        throw err;
      }
    },
    onError: (errors) => {
      console.log("Validation errors:", errors);
    },
  });

  useEffect(() => {
    fetchHarvestRecords();
    if (!cycleId) {
      fetchCurrentCycle();
    }
  }, [pondId, cycleId]);

  const fetchHarvestRecords = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getHarvestRecords(pondId, cycleId);
      setHarvestRecords(data.data || data || []);
    } catch (err) {
      setError("Failed to load harvest records");
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentCycle = async () => {
    try {
      const cycleResponse = await getCurrentCycle(pondId);
      setCurrentCycle(cycleResponse.data || cycleResponse);
    } catch (err) {
      setCurrentCycle(null);
    }
  };

  const handleOpenModal = async (record?: HarvestRecord) => {
    if (record) {
      setEditingRecord(record);
      form.setValues({
        harvestDate: record.harvestDate.split("T")[0],
        count: record.count,
        totalWeight: record.totalWeight,
        harvestType: record.harvestType,
        notes: record.notes || "",
      });
      setCurrentCycle(null); // No need to show cycle info for editing existing records
    } else {
      setEditingRecord(null);
      form.reset();

      // Fetch current cycle for new records
      try {
        const cycleResponse = await getCurrentCycle(pondId);
        setCurrentCycle(cycleResponse.data || cycleResponse);
      } catch (err) {
        console.log("Error fetching current cycle:", err);
        setCurrentCycle(null);
      }
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingRecord(null);
    form.reset();
  };


  const calculateTotalHarvested = () => {
    const cycleRecords = currentCycle
      ? harvestRecords.filter(record => record.cycleId === currentCycle.id)
      : harvestRecords;
    return cycleRecords.reduce((sum, record) => sum + (Number(record.count) || 0), 0);
  };

  const calculateTotalHarvestedWeight = () => {
    const cycleRecords = currentCycle
      ? harvestRecords.filter(record => record.cycleId === currentCycle.id)
      : harvestRecords;
    return cycleRecords.reduce((sum, record) => sum + (Number(record.totalWeight) || 0), 0);
  };

  const calculateSurvivalNumber = (count: number, totalWeight: number) => {
    return (Number(count) || 0) * (Number(totalWeight) || 0);
  };

  const getHarvestTypeColor = (type: string) => {
    return type === "final" ? "error" : "primary";
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{mb: 2}}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Box display="flex" flexDirection="column" gap={3} mb={3}>
        {/* Current Cycle Cards */}
        {currentCycle && (
          <Box>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
              {t("Current Cycle Statistics")}
            </Typography>
            <Box display="flex" gap={1} flexWrap="wrap">
              <Card sx={{minWidth: 120, flex: 1}}>
                <CardContent sx={{ py: 1, px: 2, '&:last-child': { pb: 1 } }}>
                  <Typography variant="caption" color="textSecondary" display="block">
                    {t("Harvest Records")}
                  </Typography>
                  <Typography variant="h6">
                    {harvestRecords.filter(record => record.cycleId === currentCycle.id).length}
                  </Typography>
                </CardContent>
              </Card>
              <Card sx={{minWidth: 120, flex: 1}}>
                <CardContent sx={{ py: 1, px: 2, '&:last-child': { pb: 1 } }}>
                  <Typography variant="caption" color="textSecondary" display="block">
                    {t("Shrimp Harvested")}
                  </Typography>
                  <Typography variant="h6">{calculateTotalHarvested().toLocaleString()}</Typography>
                </CardContent>
              </Card>
              <Card sx={{minWidth: 120, flex: 1}}>
                <CardContent sx={{ py: 1, px: 2, '&:last-child': { pb: 1 } }}>
                  <Typography variant="caption" color="textSecondary" display="block">
                    {t("Weight Harvested")}
                  </Typography>
                  <Typography variant="h6">{calculateTotalHarvestedWeight().toFixed(1)} kg</Typography>
                </CardContent>
              </Card>
            </Box>
          </Box>
        )}

        {/* Total/All-Time Cards */}
        <Box>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
            {t("All-Time Statistics")}
          </Typography>
          <Box display="flex" gap={1} flexWrap="wrap">
            <Card sx={{minWidth: 120, flex: 1}}>
              <CardContent sx={{ py: 1, px: 2, '&:last-child': { pb: 1 } }}>
                <Typography variant="caption" color="textSecondary" display="block">
                  {t("Harvest Records")}
                </Typography>
                <Typography variant="h6">{harvestRecords.length}</Typography>
          </CardContent>
        </Card>
            <Card sx={{minWidth: 120, flex: 1}}>
              <CardContent sx={{ py: 1, px: 2, '&:last-child': { pb: 1 } }}>
                <Typography variant="caption" color="textSecondary" display="block">
                  {t("Shrimp Harvested")}
                </Typography>
                <Typography variant="h6">
                  {harvestRecords.reduce((sum, record) => sum + (Number(record.count) || 0), 0).toLocaleString()}
            </Typography>
          </CardContent>
        </Card>
            <Card sx={{minWidth: 120, flex: 1}}>
              <CardContent sx={{ py: 1, px: 2, '&:last-child': { pb: 1 } }}>
                <Typography variant="caption" color="textSecondary" display="block">
                  {t("Weight Harvested")}
                </Typography>
                <Typography variant="h6">
                  {harvestRecords.reduce((sum, record) => sum + (Number(record.totalWeight) || 0), 0).toFixed(1)} kg
            </Typography>
          </CardContent>
        </Card>
          </Box>
        </Box>
      </Box>

      {/* Action Buttons */}
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenModal()} disabled={!isCycleActive}>
          {t("Add Harvest Record")}
        </Button>
      </Box>
      {!isCycleActive && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t("Viewing closed cycle data. Editing is disabled.")}
        </Alert>
      )}

      {/* Harvest Records Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t("Date")}</TableCell>
              <TableCell align="right">{t("Count")}</TableCell>
              <TableCell align="right">{t("Total Weight (kg)")}</TableCell>
              <TableCell align="right">{t("Survival Number")}</TableCell>
              <TableCell>{t("Type")}</TableCell>
              <TableCell>{t("Cycle")}</TableCell>
              <TableCell>{t("Notes")}</TableCell>
              <TableCell align="center">{t("Actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {harvestRecords.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography color="textSecondary">{t("No harvest records found")}</Typography>
                </TableCell>
              </TableRow>
            ) : (
              harvestRecords.map((record) => (
                <TableRow key={record.id}>
                  <TableCell>{new Date(record.harvestDate).toLocaleDateString()}</TableCell>
                  <TableCell align="right">{Number(record.count).toLocaleString()}</TableCell>
                  <TableCell align="right">{Number(record.totalWeight).toFixed(2)}</TableCell>
                  <TableCell align="right">
                    {record.survivalNumber ? Number(record.survivalNumber).toFixed(2) : calculateSurvivalNumber(record.count, record.totalWeight).toFixed(2)}
                  </TableCell>
                  <TableCell>
                    <Chip label={record.harvestType} color={getHarvestTypeColor(record.harvestType) as any} size="small" />
                  </TableCell>
                  <TableCell>
                    {record.cycle ? (
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {t("Started")}: {new Date(record.cycle.startedAt).toLocaleDateString()}
                        </Typography>
                        <Chip
                          label={record.cycle.status === 'active' ? t("Active") :
                                 record.cycle.status === 'completed' ? t("Completed") :
                                 record.cycle.status === 'terminated' ? t("Terminated") : record.cycle.status}
                          color={record.cycle.status === 'active' ? 'primary' :
                                 record.cycle.status === 'completed' ? 'success' : 'error'}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                    ) : (
                      <Typography variant="body2" color="textSecondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell>{record.notes || "-"}</TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => handleOpenModal(record)} disabled={!isCycleActive}>
                      <EditIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Harvest Record Modal */}
      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle>{editingRecord ? t("Edit Harvest Record") : t("Add Harvest Record")}</DialogTitle>
        <form onSubmit={form.handleSubmit}>
          <DialogContent>
            <Box display="flex" flexDirection="column" gap={2}>
              {/* Show current cycle information for new records */}
              {!editingRecord && currentCycle && (
                <Alert severity="info" sx={{ mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                    {t("Current Cycle Information")}
                  </Typography>
                  <Typography variant="body2">
                    <strong>{t("Started")}: </strong>
                    {currentCycle.startedAt ? new Date(currentCycle.startedAt).toLocaleDateString() : t("Not set")}
                  </Typography>
                  {currentCycle.finishedAt && (
                    <Typography variant="body2">
                      <strong>{t("Finished")}: </strong>
                      {new Date(currentCycle.finishedAt).toLocaleDateString()}
                    </Typography>
                  )}
                  <Typography variant="body2">
                    <strong>{t("Status")}: </strong>
                    {currentCycle.status === 'active' ? t("Active") :
                     currentCycle.status === 'completed' ? t("Completed") :
                     currentCycle.status === 'terminated' ? t("Terminated") : currentCycle.status}
                  </Typography>
                </Alert>
              )}

              {!editingRecord && !currentCycle && (
                <Alert severity="warning" sx={{ mb: 1 }}>
                  <Typography variant="body2">
                    {t("No active cycle found. Please create a cycle before adding harvest records.")}
                  </Typography>
                </Alert>
              )}
              <TextField
                label={t("Harvest Date")}
                type="date"
                name="harvestDate"
                value={form.values.harvestDate}
                onChange={form.handleChange("harvestDate")}
                fullWidth
                required
                error={!!form.errors.harvestDate}
                helperText={form.errors.harvestDate}
                InputLabelProps={{shrink: true}}
              />

              <TextField
                label={t("Count")}
                type="number"
                name="count"
                value={form.values.count}
                onChange={form.handleChange("count")}
                fullWidth
                required
                error={!!form.errors.count}
                helperText={form.errors.count}
                inputProps={{min: 1}}
              />

              <TextField
                label={t("Total Weight (kg)")}
                type="number"
                name="totalWeight"
                value={form.values.totalWeight}
                onChange={form.handleChange("totalWeight")}
                fullWidth
                required
                error={!!form.errors.totalWeight}
                helperText={form.errors.totalWeight}
                inputProps={{min: 0, step: 0.01}}
              />

              {/* Display calculated Survival Number */}
              {form.values.count > 0 && form.values.totalWeight > 0 && (
                <TextField
                  label={t("Survival Number")}
                  type="number"
                  value={calculateSurvivalNumber(form.values.count, form.values.totalWeight).toFixed(2)}
                  fullWidth
                  disabled
                  helperText={t("Calculated automatically (Count × Total Weight)")}
                />
              )}

              <FormControl fullWidth required error={!!form.errors.harvestType}>
                <InputLabel>{t("Harvest Type")}</InputLabel>
                <Select
                  name="harvestType"
                  value={form.values.harvestType}
                  onChange={(e) => form.handleChange("harvestType")(e as any)}
                  label={t("Harvest Type")}>
                  <MenuItem value="partial">{t("Partial")}</MenuItem>
                  <MenuItem value="final">{t("Final")}</MenuItem>
                </Select>
              </FormControl>

              {form.values.harvestType === 'final' && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    <strong>{t("Warning")}:</strong> {t("Recording a final harvest will automatically close the current production cycle.")}
                  </Typography>
                </Alert>
              )}

              <TextField
                label={t("Notes")}
                name="notes"
                value={form.values.notes}
                onChange={form.handleChange("notes")}
                fullWidth
                multiline
                rows={3}
                error={!!form.errors.notes}
                helperText={form.errors.notes}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseModal}>{t("Cancel")}</Button>
            <Button type="submit" variant="contained" disabled={form.isSubmitting}>
              {form.isSubmitting ? <CircularProgress size={20} /> : editingRecord ? t("Update") : t("Add")}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

    </Box>
  );
};

export default HarvestTab;
